import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';

import {
  ApplicationUIContext,
  AntdTag,
  Table,
  basicSort,
  AntdButton as LinkButton,
} from '@yuhuinc/ui-library';

import { Table as RawTable, Button, Tooltip } from 'antd';
import { getTranslatedString } from 'helpers/i18n';
import { IncentivesContext } from 'components/landlord_app/payments/LandlordIncentives';
import { EditOutlined } from '@ant-design/icons';

const ExpandedRow = ({ record, handleEditClicked }) => {
  return (
    <RawTable
      size="small"
      pagination={false}
      columns={[
        {
          dataIndex: 'name',
          dataType: 'text',
          key: 'name',
          ellipsis: true,
          title: getTranslatedString('landlordPortal.incentives.name'),
          width: '50%',
        },
        {
          dataIndex: 'applications',
          dataType: 'text',
          key: 'applications',
          width: '10%',
          title: getTranslatedString('landlordPortal.incentives.applications'),
          render: (_, rowData) => 0,
        },
        {
          dataIndex: 'leases',
          dataType: 'text',
          key: 'leases',
          width: '10%',
          title: getTranslatedString('landlordPortal.incentives.leases'),
          render: (_, rowData) => 0,
        },
        {
          dataIndex: 'actions',
          dataType: 'text',
          key: 'actions',
          width: '10%',
          title: getTranslatedString('landlordPortal.incentives.actions'),
          render: (_, rowData) => {
            return (
              <Tooltip title={getTranslatedString('landlordPortal.incentives.edit')}>
                <Button
                  type="link"
                  icon={<EditOutlined />}
                  onClick={() => handleEditClicked(record.id, rowData.id)}
                />
              </Tooltip>
            );
          },
        },
      ]}
      dataSource={record.incentives}
    />
  );
};

const IncentiveTemplatesTable = ({
  renderRowActions,
  actionButtonLabel,
  actionButtonOnClick,
  onEditClick,
  onResetClick,
  incentiveTemplateIncluded,
  statusFilters,
  showActionButton,
}) => {
  const incentivesData = useContext(IncentivesContext);
  const { isMobile } = useContext(ApplicationUIContext);
  const { incentiveTemplates, loading } = incentivesData;
  const [filteredInfo, setFilteredInfo] = useState(null);
  const [sortedInfo, setSortedInfo] = useState({ columnKey: 'internalName', order: 'ascend' });
  const dataSource = !!incentiveTemplateIncluded
    ? incentiveTemplates.filter((f) => incentiveTemplateIncluded(f))
    : incentiveTemplates;

  const handleTableChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  const handleResetButtonClick = () => {
    setSortedInfo(null);
    setFilteredInfo(null);
    if (!!onResetClick) onResetClick();
  };

  return (
    <Table
      onChange={handleTableChange}
      dataSource={dataSource}
      loading={!!loading}
      pagination={{
        total: dataSource.length,
      }}
      showActionButton={showActionButton}
      actionButtonLabel={actionButtonLabel}
      actionButtonOnClick={actionButtonOnClick}
      resetButtonOnClick={handleResetButtonClick}
      actionAndSearchStyle={{ padding: 0 }}
      title={null}
      scroll={{
        x: isMobile,
      }}
      expandedRowRender={(record) => (
        <ExpandedRow record={record} handleEditClicked={onEditClick} />
      )}
      rowExpandable={(record) => record.incentives.length > 0}
      columns={[
        {
          dataIndex: 'internal_name',
          dataType: 'text',
          key: 'internalName',
          searchable: true,
          ellipsis: true,
          title: getTranslatedString('landlordPortal.incentives.name'),
          width: '35%',
          sortOrder: sortedInfo?.columnKey === 'internalName' && sortedInfo?.order,
          sorter: (a, b) => basicSort(a.internal_name, b.internal_name),
          render: (_, rowData) => {
            return (
              <LinkButton
                type="link"
                label={rowData.internal_name}
                size="small"
                onClick={() => onEditClick(rowData.id)}
              />
            );
          },
        },
        {
          dataIndex: 'status',
          key: 'status',
          searchable: true,
          title: getTranslatedString('landlordPortal.incentives.status'),
          onFilter: (value, record) => record.status.indexOf(value) === 0,
          filteredValue: filteredInfo?.status || null,
          render: (status, rowData) => <AntdTag status={status} />,
          filters: statusFilters,
        },
        {
          dataIndex: 'incentives',
          key: 'incentives',
          title: getTranslatedString('landlordPortal.incentives.incentives'),
          sortOrder: sortedInfo?.columnKey === 'incentives' && sortedInfo?.order,
          sorter: (a, b) => basicSort(a.incentives.length, b.incentives.length),
          render: (incentives, rowData) => rowData.incentives.length,
        },
        {
          dataIndex: 'applications',
          key: 'applications',
          title: getTranslatedString('landlordPortal.incentives.applications'),
          render: (_, rowData) => 0,
        },
        {
          dataIndex: 'leases',
          key: 'leases',
          title: getTranslatedString('landlordPortal.incentives.leases'),
          render: (_, rowData) => 0,
        },
        {
          key: 'actions',
          title: getTranslatedString('landlordPortal.incentives.actions'),
          searchable: false,
          render: (_, rowData) => renderRowActions(rowData),
        },
      ]}
    />
  );
};

IncentiveTemplatesTable.propTypes = {
  actionButtonLabel: PropTypes.string.isRequired,
  actionButtonOnClick: PropTypes.func.isRequired,
  onEditClick: PropTypes.func.isRequired,
  onResetClick: PropTypes.func,

  renderRowActions: PropTypes.func.isRequired,
  statusFilters: PropTypes.array,

  // Run this function against all fee_templates in context
  // to decide if they belong on this table.
  feeTemplateIncluded: PropTypes.func,
};

IncentiveTemplatesTable.defaultProps = {
  statusFilters: null,
};

export default IncentiveTemplatesTable;
